import useDebounce from 'hooks/helpers/UseDebounce';
import { useIntersection } from 'hooks/ui/useIntersection';
import { useAnchor } from 'hooks/url/useAnchor';
import { useEffect, useRef, useState } from 'react';

export const PRODUCT_DETAIL_YOUTUBE_VIDEO_ID = 'product-detail-video';

type YouTubeVideoProps = {
    youtubeVideoId: string | null;
};

const Content: FC<YouTubeVideoProps> = ({ youtubeVideoId }) => {
    const anchor = useAnchor();
    const videoWrapperRef = useRef<HTMLDivElement>(null);
    const intersection = useIntersection(videoWrapperRef);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const isAlreadyLoaded = useRef(false);
    const scrollToVideo = useDebounce(anchor === 'video' && !!videoWrapperRef.current, 500);

    useEffect(() => {
        if (scrollToVideo && videoWrapperRef.current) {
            videoWrapperRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            });
        }
    }, [scrollToVideo]);

    useEffect(() => {
        if (!isAlreadyLoaded.current && intersection?.isIntersecting) {
            setShouldLoadVideo(true);
            isAlreadyLoaded.current = true;
        }
    }, [intersection?.isIntersecting]);

    return (
        <div className="relative h-0 overflow-hidden bg-grayLight pb-[56.25%]" id={PRODUCT_DETAIL_YOUTUBE_VIDEO_ID}>
            <div className="scroll-mt-20" ref={videoWrapperRef}>
                {shouldLoadVideo && (
                    <iframe
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute top-0 left-0 h-full w-full"
                        frameBorder="0"
                        height="480"
                        loading="lazy"
                        src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                        title="Embedded youtube"
                        width="853"
                    />
                )}
            </div>
        </div>
    );
};
export const YouTubeVideo: FC<YouTubeVideoProps> = ({ youtubeVideoId }) => {
    if (youtubeVideoId === null) {
        return null;
    }

    return <Content youtubeVideoId={youtubeVideoId} />;
};
